import * as validators from 'yup-intl-validator'

// ///////////////////////
// MARK: CONFIG
export const LOCALES_LIST = ['en-GB', 'fr-FR', 'nl-NL'] as const
export const LOCALES_LIST_UNTYPED = LOCALES_LIST as unknown as string[]

export type TLocales = (typeof LOCALES_LIST)[number]

// Map a browser language (e.g. 'fr-BE', 'nl', 'en-US') to a supported locale.
const LANGUAGE_SUBTAG_MAP: Record<string, TLocales> = {
  en: 'en-GB',
  fr: 'fr-FR',
  nl: 'nl-NL',
}

// Legacy locale codes previously stored in user state — migrated on load.
const LEGACY_LOCALE_MAP: Record<string, TLocales> = {
  'fr-BE': 'fr-FR',
  'nl-BE': 'nl-NL',
}

export type TLocalizedType<T> = {
  [key in TLocales]: T
}

// ///////////////////////
// MARK: CONTEXT
export interface IAppLocaleState {
  isSet: boolean
  active: TLocales
}

export interface IAppLocaleStateStored {
  isSet: boolean
  active: string
}

export interface IAppLocaleModel {}

export interface IAppLocaleFunctions {
  change(id: TLocales): void
  reset(): void
}

export interface IAppLocaleContext extends IAppLocaleState, IAppLocaleModel, IAppLocaleFunctions {}

export function getAppLocaleContext(): IAppLocaleModel {
  return {}
}

// ///////////////////////
// MARK: CONTEXT FUNCTIONS
export function getAppLocaleContextFunctions(
  state: IAppLocaleState,
  setState: React.Dispatch<React.SetStateAction<IAppLocaleState>>,
): IAppLocaleFunctions {
  return {
    change(id: TLocales) {
      setState((oldState) => ({
        ...oldState,
        isSet: true,
        active: id,
      }))
    },
    reset() {
      setState((oldState) => ({
        ...oldState,
        isSet: false,
        active: 'en-GB',
      }))
    },
  }
}

// ///////////////////////
// MARK: INITIAL STATE
export function getAppLocaleInitialState(key = 'state_locale'): IAppLocaleState {
  let active: TLocales = 'en-GB'

  const browser = typeof navigator !== 'undefined' ? navigator.language : ''

  if (LOCALES_LIST_UNTYPED.includes(browser)) {
    active = browser as TLocales
  } else {
    const subtag = browser.split('-')[0]?.toLowerCase() ?? ''
    if (subtag && LANGUAGE_SUBTAG_MAP[subtag]) {
      active = LANGUAGE_SUBTAG_MAP[subtag]
    }
  }

  return {
    isSet: false,
    active,
  }
}

export function parseAppLocaleState(value: IAppLocaleStateStored): IAppLocaleState {
  const migrated = LEGACY_LOCALE_MAP[value.active] ?? value.active
  return {
    isSet: value.isSet,
    active: migrated as TLocales,
  }
}

export function validateAppLocaleState(value: IAppLocaleStateStored): IAppLocaleState {
  return validators.object
    .schema(
      {
        isSet: validators.boolean.schema(
          validators.i18n.DEFAULT_INTL,
          validators.boolean.isRequired(),
        ),
        active: validators.string.schema(
          validators.i18n.DEFAULT_INTL,
          validators.string.isRequired(),
          validators.string.isEqualTo({
            values: LOCALES_LIST_UNTYPED,
          }),
        ),
      },
      validators.i18n.DEFAULT_INTL,
      validators.object.isRequired(),
    )
    .validateSync(value) as unknown as IAppLocaleState
}
